import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, HStack, VStack, Text, Image } from '@chakra-ui/react';
import {
  FiFileText,
  FiType,
  FiAlignLeft,
  FiMonitor,
  FiEye,
  FiTag,
  FiUsers,
  FiMapPin,
  FiHome,
  FiGlobe,
  FiLink,
  FiImage,
  FiMaximize2,
  FiShield,
  FiDollarSign,
  FiPaperclip,
  FiDownload,
  FiMessageSquare,
  FiHelpCircle,
} from 'react-icons/fi';
import EventAttachmentPreviewModal from './EventAttachmentPreviewModal';
import { iconForAttachment, formatFileSize } from './attachmentMeta';
import { EVENT_DISCLOSURES, DISCLOSURE_YES, GILLYREACH_DISCLOSURE, SOCIAL_NETWORKS } from './eventConstants';
import { parseCoverImage, attachmentKind, downloadAttachment, fetchAttachmentObjectUrl } from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Shared read-only event detail panels.
//
// These render the exact fields an advisor filled in, in the same order and
// under the same labels as the create/edit wizard. They are consumed by both
// the reviewer's full-page review (`EventReviewPage`) and the advisor's
// read-only details modal (`EventDetailsModal`), so both audiences read back
// identical content. Nothing here is editable — it's a faithful read-back.
// ─────────────────────────────────────────────────────────────────────────────

const FORMAT_META = {
  in_person: {
    label: 'In person',
    color: 'rgba(122,226,161,0.92)',
    bg: 'rgba(122,226,161,0.07)',
    border: 'rgba(122,226,161,0.18)',
  },
  online: {
    label: 'Online',
    color: 'rgba(99,179,237,0.92)',
    bg: 'rgba(99,179,237,0.07)',
    border: 'rgba(99,179,237,0.18)',
  },
  hybrid: {
    label: 'Hybrid',
    color: 'rgba(167,139,250,0.92)',
    bg: 'rgba(167,139,250,0.07)',
    border: 'rgba(167,139,250,0.18)',
  },
};

const VISIBILITY_META = {
  public: {
    label: 'Public',
    color: 'rgba(255,255,255,0.65)',
    bg: 'rgba(255,255,255,0.05)',
    border: 'rgba(255,255,255,0.10)',
  },
  private: {
    label: 'Private',
    color: 'rgba(248,113,113,0.92)',
    bg: 'rgba(248,113,113,0.07)',
    border: 'rgba(248,113,113,0.18)',
  },
};

const DetailChip = ({ color, bg, border, children }) => (
  <Box
    display="inline-flex"
    alignItems="center"
    px={2.5}
    py="3px"
    borderRadius="full"
    bg={bg}
    border={`1px solid ${border}`}
  >
    <Text fontSize="12px" fontWeight="600" color={color} letterSpacing="0.01em">
      {children}
    </Text>
  </Box>
);

const PropRow = ({ icon, label, children, last, amber }) => (
  <Flex
    align="flex-start"
    gap={4}
    py={amber ? 3.5 : 2.5}
    px={amber ? 3 : 0}
    bg={amber ? 'rgba(251,191,36,0.04)' : 'transparent'}
    borderLeft={amber ? '2px solid rgba(251,191,36,0.45)' : 'none'}
    borderBottom={last ? 'none' : '1px solid rgba(255,255,255,0.045)'}
  >
    <HStack spacing={2} w="118px" minW="118px" flexShrink={0} pt="2px">
      <Box color={amber ? 'rgba(251,191,36,0.6)' : 'rgba(255,255,255,0.28)'} flexShrink={0}>
        {icon}
      </Box>
      <Text
        fontSize="xs"
        color={amber ? 'rgba(251,191,36,0.72)' : 'rgba(255,255,255,0.38)'}
        fontWeight="500"
        noOfLines={1}
      >
        {label}
      </Text>
    </HStack>
    <Box flex={1} pt="1px" minW={0}>
      {children}
    </Box>
  </Flex>
);

export const EventDetailsPanel = ({ event, onViewCover }) => {
  const formatMeta =
    FORMAT_META[event.format] || {
      label: event.format || 'In person',
      ...FORMAT_META.in_person,
    };
  const visibilityMeta =
    VISIBILITY_META[event.visibility] || VISIBILITY_META.public;
  const isOnline = event.format === 'online' || event.format === 'hybrid';
  const isInPerson = event.format === 'in_person' || event.format === 'hybrid';
  const cover = event.coverImage ? parseCoverImage(event.coverImage) : null;

  // Ordered to mirror the create/edit wizard so the reader sees the exact
  // same fields, in the same order, under the same labels.
  const rows = [
    cover && {
      icon: <FiImage size={13} />,
      label: 'Cover photo',
      render: () => (
        <Box
          role="button"
          aria-label="View full cover photo"
          onClick={() => onViewCover?.(cover.src)}
          position="relative"
          w="220px"
          maxW="100%"
          borderRadius="10px"
          overflow="hidden"
          border="1px solid rgba(255,255,255,0.12)"
          cursor="pointer"
          sx={{ aspectRatio: '2 / 1', '&:hover .cover-zoom': { opacity: 1 } }}
        >
          <Image
            src={cover.src}
            alt="Event cover"
            w="100%"
            h="100%"
            objectFit="cover"
            objectPosition={cover.objectPosition}
          />
          <Flex
            className="cover-zoom"
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg="rgba(0,0,0,0.4)"
            opacity={0}
            transition="opacity 0.15s"
          >
            <HStack spacing={1.5} px={2.5} py={1} borderRadius="full" bg="rgba(0,0,0,0.6)" border="1px solid rgba(255,255,255,0.22)">
              <FiMaximize2 size={11} color="#fafafa" />
              <Text fontSize="11px" color="#fafafa" fontWeight="600">View full photo</Text>
            </HStack>
          </Flex>
        </Box>
      ),
    },
    event.title && {
      icon: <FiType size={13} />,
      label: 'Event title',
      render: () => (
        <Text fontSize="sm" color="#fafafa" fontWeight="500" lineHeight="1.5">
          {event.title}
        </Text>
      ),
    },
    event.summary && {
      icon: <FiAlignLeft size={13} />,
      label: 'Summary',
      render: () => (
        <Text fontSize="sm" color="rgba(255,255,255,0.72)" lineHeight="1.65">
          {event.summary}
        </Text>
      ),
    },
    {
      icon: <FiMonitor size={13} />,
      label: 'Format',
      render: () => (
        <DetailChip {...formatMeta}>{formatMeta.label}</DetailChip>
      ),
    },
    isOnline && event.onlineUrl && {
      icon: <FiLink size={13} />,
      label: 'Online meeting link',
      render: () => (
        <Text
          as="a"
          href={event.onlineUrl}
          target="_blank"
          rel="noopener noreferrer"
          fontSize="sm"
          color="rgba(122,226,161,0.85)"
          textDecoration="underline"
          textDecorationColor="rgba(122,226,161,0.3)"
          _hover={{ color: 'rgba(122,226,161,1)' }}
          noOfLines={1}
          display="block"
        >
          {event.onlineUrl}
        </Text>
      ),
    },
    event.description && {
      icon: <FiFileText size={13} />,
      label: 'Event description',
      render: () => (
        <Box>
          <Text
            fontSize="sm"
            color="rgba(255,255,255,0.72)"
            lineHeight="1.7"
            whiteSpace="pre-wrap"
          >
            {event.description}
          </Text>
          <Box
            mt={3}
            pt={3}
            borderTop="1px solid rgba(255,255,255,0.07)"
          >
            <HStack spacing={1.5} mb={1.5}>
              <Box color="rgba(251,191,36,0.7)" display="flex" alignItems="center">
                <FiShield size={11} />
              </Box>
              <Text fontSize="xs" fontWeight="600" color="rgba(251,191,36,0.7)" letterSpacing="0.03em">
                Required disclosure · always shown to attendees
              </Text>
            </HStack>
            <Text fontSize="xs" color="rgba(255,255,255,0.42)" lineHeight="1.7" whiteSpace="pre-wrap">
              {GILLYREACH_DISCLOSURE}
            </Text>
          </Box>
        </Box>
      ),
    },
    event.category && {
      icon: <FiTag size={13} />,
      label: 'Category',
      render: () => (
        <DetailChip
          color="rgba(255,255,255,0.65)"
          bg="rgba(255,255,255,0.05)"
          border="rgba(255,255,255,0.10)"
        >
          {event.category}
        </DetailChip>
      ),
    },
    event.expectedAttendees && {
      icon: <FiUsers size={13} />,
      label: 'Expected attendees',
      render: () => (
        <Text fontSize="sm" color="rgba(255,255,255,0.72)">
          Up to {event.expectedAttendees} guests
        </Text>
      ),
    },
    {
      icon: <FiEye size={13} />,
      label: 'Visibility',
      render: () => (
        <DetailChip {...visibilityMeta}>{visibilityMeta.label}</DetailChip>
      ),
    },
    isInPerson && event.venueName && {
      icon: <FiMapPin size={13} />,
      label: 'Venue',
      render: () => (
        <Text fontSize="sm" color="rgba(255,255,255,0.72)">
          {event.venueName}
        </Text>
      ),
    },
    event.address && {
      icon: <FiHome size={13} />,
      label: 'Address',
      render: () => (
        <Text fontSize="sm" color="rgba(255,255,255,0.72)">
          {event.address}
        </Text>
      ),
    },
    event.city && {
      icon: <FiGlobe size={13} />,
      label: 'City',
      render: () => (
        <Text fontSize="sm" color="rgba(255,255,255,0.72)">
          {event.city}
        </Text>
      ),
    },
  ].filter(Boolean);

  return (
    <Box mb={6}>
      {rows.length > 0 && (
        <Box
          bg="rgba(255,255,255,0.015)"
          border="1px solid rgba(255,255,255,0.07)"
          borderRadius="14px"
          px={4}
          pt={1}
          pb={1}
        >
          {rows.map((row, i) => (
            <PropRow
              key={row.label}
              icon={row.icon}
              label={row.label}
              amber={row.amber}
              last={i === rows.length - 1}
            >
              {row.render()}
            </PropRow>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Compliance disclosures (read-back) ──────────────────────────────────────

const DisclosureAnswerChip = ({ isYes }) => (
  <DetailChip
    color={isYes ? 'rgba(251,191,36,0.95)' : 'rgba(122,226,161,0.92)'}
    bg={isYes ? 'rgba(251,191,36,0.08)' : 'rgba(122,226,161,0.07)'}
    border={isYes ? 'rgba(251,191,36,0.28)' : 'rgba(122,226,161,0.18)'}
  >
    {isYes ? 'Yes' : 'No'}
  </DetailChip>
);

export const ComplianceDisclosuresPanel = ({ disclosures, pricingType, totalEventValue }) => {
  const data = disclosures || {};
  const isFree = pricingType === 'free';

  return (
    <Box mb={6}>
      <HStack spacing={2} mb={2.5}>
        <Box color="rgba(251,191,36,0.7)">
          <FiShield size={13} />
        </Box>
        <Text
          fontSize="xs"
          fontWeight="600"
          color="rgba(251,191,36,0.85)"
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          Compliance disclosures
        </Text>
      </HStack>

      <Box
        bg="rgba(251,191,36,0.025)"
        border="1px solid rgba(251,191,36,0.14)"
        borderRadius="14px"
        px={4}
        pt={1}
        pb={1}
      >
        {EVENT_DISCLOSURES.map((block, i) => {
          const value = data[block.key] || {};
          const answered = value.answer === DISCLOSURE_YES || value.answer === 'no';
          const isYes = value.answer === DISCLOSURE_YES;
          const isLastDisclosure = i === EVENT_DISCLOSURES.length - 1;
          const hasMore = !isLastDisclosure || isFree;
          return (
            <Box
              key={block.key}
              py={3.5}
              borderBottom={hasMore ? '1px solid rgba(255,255,255,0.045)' : 'none'}
            >
              <Flex align="flex-start" justify="space-between" gap={4} mb={isYes ? 3 : 0}>
                <Text fontSize="sm" color="rgba(255,255,255,0.72)" lineHeight="1.5">
                  {block.gateLabel}
                </Text>
                <Box flexShrink={0} pt="1px">
                  {answered ? (
                    <DisclosureAnswerChip isYes={isYes} />
                  ) : (
                    <Text fontSize="xs" color="rgba(255,255,255,0.3)" fontStyle="italic">
                      Not disclosed
                    </Text>
                  )}
                </Box>
              </Flex>

              {isYes && (
                <HStack spacing={6} pl={1} flexWrap="wrap" rowGap={2}>
                  <HStack spacing={2} align="center">
                    <Box color="rgba(255,255,255,0.28)">
                      <FiUsers size={12} />
                    </Box>
                    <Text fontSize="xs" color="rgba(255,255,255,0.4)">
                      {block.payerLabel.replace(/\?$/, '')}:
                    </Text>
                    <Text fontSize="sm" color="#fafafa" fontWeight="500">
                      {value.payer || '—'}
                    </Text>
                  </HStack>
                  {!block.noAmount && (
                    <HStack spacing={2} align="center">
                      <Box color="rgba(255,255,255,0.28)">
                        <FiDollarSign size={12} />
                      </Box>
                      <Text fontSize="xs" color="rgba(255,255,255,0.4)">
                        {block.amountLabel}:
                      </Text>
                      <Text fontSize="sm" color="#fafafa" fontWeight="500">
                        {value.amount ? `$${value.amount}` : '—'}
                      </Text>
                    </HStack>
                  )}
                </HStack>
              )}
            </Box>
          );
        })}

        {isFree && (
          <Box py={3.5}>
            <Flex align="flex-start" justify="space-between" gap={4}>
              <Text fontSize="sm" color="rgba(255,255,255,0.72)" lineHeight="1.5">
                Total dollar value of event
              </Text>
              <HStack spacing={2} align="center" flexShrink={0}>
                <Box color="rgba(255,255,255,0.28)">
                  <FiDollarSign size={12} />
                </Box>
                <Text fontSize="sm" color="#fafafa" fontWeight="500">
                  {totalEventValue ? `$${totalEventValue}` : '—'}
                </Text>
              </HStack>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Compliance notes (free-form advisor context) ────────────────────────────

export const ComplianceNotesPanel = ({ notes }) => {
  if (!notes) return null;

  return (
    <Box mb={6}>
      <HStack spacing={2} mb={2.5}>
        <Box color="rgba(255,255,255,0.35)">
          <FiAlignLeft size={13} />
        </Box>
        <Text
          fontSize="xs"
          fontWeight="600"
          color="rgba(255,255,255,0.5)"
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          Anything else compliance should know
        </Text>
      </HStack>

      <Box
        bg="rgba(255,255,255,0.015)"
        border="1px solid rgba(255,255,255,0.07)"
        borderRadius="14px"
        px={4}
        py={4}
      >
        <Text fontSize="sm" color="rgba(255,255,255,0.72)" lineHeight="1.7" whiteSpace="pre-wrap">
          {notes}
        </Text>
      </Box>
    </Box>
  );
};

// ─── Qualification questions panel ───────────────────────────────────────────

export const QualificationQuestionsPanel = ({ questions }) => {
  const items = Array.isArray(questions) ? questions.filter(Boolean) : [];
  if (items.length === 0) return null;

  return (
    <Box mb={6}>
      <HStack spacing={2} mb={2.5}>
        <Box color="rgba(167,139,250,0.7)">
          <FiHelpCircle size={13} />
        </Box>
        <Text
          fontSize="xs"
          fontWeight="600"
          color="rgba(167,139,250,0.85)"
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          Qualification questions
        </Text>
      </HStack>

      <Box
        bg="rgba(255,255,255,0.015)"
        border="1px solid rgba(255,255,255,0.07)"
        borderRadius="14px"
        px={4}
        pt={1}
        pb={1}
      >
        {items.map((q, i) => (
          <HStack
            key={i}
            spacing={3}
            py={3}
            borderBottom={i < items.length - 1 ? '1px solid rgba(255,255,255,0.045)' : 'none'}
            align="flex-start"
          >
            <Text
              fontSize="xs"
              fontWeight="600"
              color="rgba(167,139,250,0.7)"
              minW="18px"
              pt="1px"
            >
              {i + 1}.
            </Text>
            <Text fontSize="sm" color="rgba(255,255,255,0.82)" lineHeight="1.6">
              {q}
            </Text>
          </HStack>
        ))}
      </Box>
    </Box>
  );
};

// ─── Promotion panel (social posts + flyers) ─────────────────────────────────

const NETWORK_LABEL = Object.fromEntries(SOCIAL_NETWORKS.map((n) => [n.value, n.label]));

const NETWORK_COLOR = {
  x:         { color: '#fafafa',              bg: 'rgba(255,255,255,0.10)', border: 'rgba(255,255,255,0.18)' },
  instagram: { color: 'rgba(249,168,212,0.95)', bg: 'rgba(249,168,212,0.08)', border: 'rgba(249,168,212,0.22)' },
  facebook:  { color: 'rgba(99,179,237,0.95)',  bg: 'rgba(99,179,237,0.08)',  border: 'rgba(99,179,237,0.22)'  },
  linkedin:  { color: 'rgba(96,165,250,0.95)',  bg: 'rgba(96,165,250,0.08)',  border: 'rgba(96,165,250,0.22)'  },
};

const AuthedPostImage = ({ url, alt }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!url) return;
    let revoked = false;
    fetchAttachmentObjectUrl(url)
      .then(({ objectUrl }) => { if (!revoked) setSrc(objectUrl); })
      .catch(() => {});
    return () => {
      revoked = true;
      if (src) URL.revokeObjectURL(src);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <Box
      position="relative"
      w="100%"
      sx={{ aspectRatio: '1 / 1' }}
      bg="#000"
      overflow="hidden"
    >
      <Box
        as="img"
        src={src || ''}
        alt={alt || ''}
        position="absolute"
        inset={0}
        w="100%"
        h="100%"
        objectFit="contain"
        display="block"
      />
    </Box>
  );
};

const SocialPostPreview = ({ post }) => {
  const networks = Array.isArray(post.networks)
    ? post.networks
    : post.network ? [post.network] : [];

  return (
    <Box
      bg="#0f0f0f"
      border="1px solid rgba(255,255,255,0.10)"
      borderRadius="12px"
      overflow="hidden"
    >
      {networks.length > 0 && (
        <HStack spacing={1.5} px={3} pt={3} pb={post.photo || post.caption ? 0 : 3} flexWrap="wrap" rowGap={1.5}>
          {networks.map((n) => {
            const c = NETWORK_COLOR[n] || { color: 'rgba(167,139,250,0.9)', bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.22)' };
            return (
              <Box key={n} px={2.5} py="2px" borderRadius="full" bg={c.bg} border={`1px solid ${c.border}`}>
                <Text fontSize="11px" fontWeight="700" color={c.color} letterSpacing="0.02em">
                  {NETWORK_LABEL[n] || n}
                </Text>
              </Box>
            );
          })}
        </HStack>
      )}

      {post.photo && (
        <Box mt={networks.length > 0 ? 3 : 0} bg="#000">
          <AuthedPostImage url={post.photo.url} alt={post.photo.name || 'Post photo'} />
        </Box>
      )}

      {post.caption && (
        <Box px={3} pt={2.5} pb={3}>
          <Text fontSize="10px" fontWeight="600" color="rgba(255,255,255,0.35)" letterSpacing="0.06em" textTransform="uppercase" mb={1}>
            Caption
          </Text>
          <Text fontSize="sm" color="rgba(255,255,255,0.85)" lineHeight="1.7" whiteSpace="pre-wrap">
            {post.caption}
          </Text>
        </Box>
      )}

      {!networks.length && !post.photo && !post.caption && (
        <Box px={3} py={3}>
          <Text fontSize="sm" color="rgba(255,255,255,0.3)" fontStyle="italic">No content</Text>
        </Box>
      )}
    </Box>
  );
};

export const PromotionPanel = ({ socialPosts, promotionFlyers }) => {
  const [flyerPreview, setFlyerPreview] = useState(null);
  const posts = Array.isArray(socialPosts) ? socialPosts : [];
  const flyers = Array.isArray(promotionFlyers) ? promotionFlyers : [];
  if (posts.length === 0 && flyers.length === 0) return null;

  return (
    <Box mb={6}>
      <HStack spacing={2} mb={2.5}>
        <Box color="rgba(167,139,250,0.7)">
          <FiMessageSquare size={13} />
        </Box>
        <Text
          fontSize="xs"
          fontWeight="600"
          color="rgba(167,139,250,0.85)"
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          Promotion
        </Text>
      </HStack>

      <VStack spacing={4} align="stretch">
        {posts.length > 0 && (
          <VStack spacing={3} align="flex-start">
            {posts.map((post, i) => (
              <Box key={post._id || i} w="100%" maxW="400px">
                <SocialPostPreview post={post} />
              </Box>
            ))}
          </VStack>
        )}

        {flyers.length > 0 && (
          <Box
            bg="rgba(255,255,255,0.015)"
            border="1px solid rgba(255,255,255,0.07)"
            borderRadius="14px"
            px={4}
            pt={1}
            pb={1}
          >
            <Box py={2.5} borderBottom="1px solid rgba(255,255,255,0.045)">
              <Text fontSize="xs" fontWeight="600" color="rgba(255,255,255,0.38)" letterSpacing="0.04em" textTransform="uppercase">
                Flyers &amp; Posters
              </Text>
            </Box>
            {flyers.map((item, i) => {
              const last = i === flyers.length - 1;
              const previewable = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'].includes(item.contentType);
              return (
                <HStack
                  key={item.id || i}
                  spacing={3}
                  py={3}
                  borderBottom={last ? 'none' : '1px solid rgba(255,255,255,0.045)'}
                >
                  <Box color="rgba(255,255,255,0.5)" flexShrink={0} display="flex" alignItems="center">
                    <FiPaperclip size={14} />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" color="rgba(255,255,255,0.85)" noOfLines={1}>{item.name}</Text>
                    {item.size ? (
                      <Text fontSize="xs" color="rgba(255,255,255,0.38)">
                        {(item.size / (1024 * 1024)).toFixed(1)} MB
                      </Text>
                    ) : null}
                  </Box>
                  <Button
                    size="xs"
                    variant="ghost"
                    leftIcon={previewable ? <FiEye size={13} /> : <FiDownload size={13} />}
                    color="rgba(255,255,255,0.6)"
                    fontWeight="500"
                    borderRadius="full"
                    _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={() => previewable ? setFlyerPreview(item) : downloadAttachment(item.url, item.name)}
                  >
                    {previewable ? 'Preview' : 'Download'}
                  </Button>
                </HStack>
              );
            })}
          </Box>
        )}
      </VStack>

      <EventAttachmentPreviewModal
        isOpen={!!flyerPreview}
        attachment={flyerPreview}
        onClose={() => setFlyerPreview(null)}
      />
    </Box>
  );
};

// ─── Event materials (advisor-uploaded attachments) ──────────────────────────

export const EventMaterialsPanel = ({ attachments }) => {
  const [preview, setPreview] = useState(null);
  const items = Array.isArray(attachments) ? attachments : [];
  if (items.length === 0) return null;

  return (
    <Box mb={6}>
      <HStack spacing={2} mb={2.5}>
        <Box color="rgba(99,179,237,0.7)">
          <FiPaperclip size={13} />
        </Box>
        <Text
          fontSize="xs"
          fontWeight="600"
          color="rgba(99,179,237,0.85)"
          letterSpacing="0.04em"
          textTransform="uppercase"
        >
          Event materials
        </Text>
      </HStack>

      <Box
        bg="rgba(255,255,255,0.015)"
        border="1px solid rgba(255,255,255,0.07)"
        borderRadius="14px"
        px={4}
        pt={1}
        pb={1}
      >
        {items.map((item, i) => {
          const Icon = iconForAttachment(item.contentType, item.name);
          const previewable = ['image', 'pdf'].includes(attachmentKind(item.contentType, item.name));
          const last = i === items.length - 1;
          return (
            <HStack
              key={item.id || item.url}
              spacing={3}
              py={3}
              borderBottom={last ? 'none' : '1px solid rgba(255,255,255,0.045)'}
            >
              <Box color="rgba(255,255,255,0.5)" flexShrink={0} display="flex" alignItems="center">
                <Icon size={15} />
              </Box>
              <Box flex={1} minW={0}>
                <Text fontSize="sm" color="rgba(255,255,255,0.85)" noOfLines={1}>
                  {item.name}
                </Text>
                {item.size ? (
                  <Text fontSize="xs" color="rgba(255,255,255,0.38)">
                    {formatFileSize(item.size)}
                  </Text>
                ) : null}
              </Box>
              <Button
                size="xs"
                variant="ghost"
                leftIcon={previewable ? <FiEye size={13} /> : <FiDownload size={13} />}
                color="rgba(255,255,255,0.6)"
                fontWeight="500"
                borderRadius="full"
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none' }}
                onClick={() =>
                  previewable ? setPreview(item) : downloadAttachment(item.url, item.name)
                }
              >
                {previewable ? 'Preview' : 'Download'}
              </Button>
            </HStack>
          );
        })}
      </Box>

      <EventAttachmentPreviewModal
        isOpen={!!preview}
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </Box>
  );
};

/**
 * Composite read-only details. Renders every advisor-filled section in the
 * same order as the wizard, so the advisor (post-approval) can copy these
 * details verbatim onto Eventbrite / Luma. Nothing is editable.
 */
const EventDetailsView = ({ event, onViewCover }) => {
  if (!event) return null;
  return (
    <Box>
      <EventDetailsPanel event={event} onViewCover={onViewCover} />
      <QualificationQuestionsPanel questions={event.qualificationQuestions} />
      <ComplianceDisclosuresPanel
        disclosures={event.complianceDisclosures}
        pricingType={event.pricingType}
        totalEventValue={event.totalEventValue}
      />
      <PromotionPanel
        socialPosts={event.socialPosts}
        promotionFlyers={event.promotionFlyers}
      />
      <ComplianceNotesPanel notes={event.complianceNotes} />
      <EventMaterialsPanel attachments={event.complianceAttachments} />
    </Box>
  );
};

export default EventDetailsView;
